
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ChatGroup, ChatMessage } from '@/types/chat';
import { UnifiedUser } from '@/hooks/useUnifiedUser';
import ChatSidebar from './ChatSidebar';
import ChatMessagesArea from './ChatMessagesArea';
import CreateChatModal from './CreateChatModal';

interface DesktopChatLayoutProps {
  user: UnifiedUser;
  userType: 'supabase' | 'internal';
  chatGroups: ChatGroup[];
  selectedGroup: ChatGroup | null;
  messages: ChatMessage[];
  sendMessage: (message: string, parentMessageId?: string) => Promise<void>;
  selectGroup: (group: ChatGroup) => Promise<void>;
  canCreateGroup: boolean;
  highlightMessageId?: string;
  onJumpToMention?: (messageId: string) => void;
}

const DesktopChatLayout = ({
  user,
  userType,
  chatGroups,
  selectedGroup,
  messages,
  sendMessage,
  selectGroup,
  canCreateGroup,
  highlightMessageId,
  onJumpToMention
}: DesktopChatLayoutProps) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [jumpToMessageId, setJumpToMessageId] = useState<string | undefined>(highlightMessageId);
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);

  // Debug logging for desktop chat state
  useEffect(() => {
    console.log('=== DESKTOP CHAT LAYOUT STATE UPDATE ===');
    console.log('User:', user?.username, 'Type:', userType);
    console.log('Selected Group:', selectedGroup?.chat_name, selectedGroup?.chat_id);
    console.log('Messages Count:', messages.length);
    console.log('Chat Groups Count:', chatGroups.length);
    
    if (messages.length > 0) {
      console.log('Recent messages:', messages.slice(-3).map(m => ({
        id: m.message_id,
        user: m.user_profile?.username,
        message: m.message.substring(0, 50),
        timestamp: m.created_at
      })));
    }
  }, [user, selectedGroup, messages, chatGroups]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedGroup || !user) {
      console.log('❌ Desktop: Cannot send message - missing data:', { 
        hasMessage: !!newMessage.trim(), 
        hasGroup: !!selectedGroup, 
        hasUser: !!user,
        message: newMessage,
        groupId: selectedGroup?.chat_id,
        userId: user?.id
      });
      return;
    }
    
    console.log('=== DESKTOP SENDING MESSAGE ===');
    console.log('Message:', newMessage);
    console.log('Replying to:', replyingToMessageId || 'none (root message)');
    console.log('Selected group:', selectedGroup?.chat_name, selectedGroup?.chat_id);
    console.log('User:', user.username, user.id);
    console.log('sendMessage function type:', typeof sendMessage);
    
    try {
      console.log('🔄 Desktop: Calling sendMessage...');
      await sendMessage(newMessage, replyingToMessageId || undefined);
      console.log('🔄 Desktop: sendMessage completed, clearing input...');
      setNewMessage('');
      setReplyingToMessageId(null); // Clear reply context
      console.log('✅ Desktop message sent successfully');
    } catch (error) {
      console.error('❌ Desktop: Error sending message:', error);
    }
  };

  const handleCreateGroup = () => {
    setShowCreateModal(true);
  };

  const handleGroupCreated = () => {
    setShowCreateModal(false);
  };

  const handleJumpToMention = (messageId: string) => {
    console.log('🎯 Desktop: Jumping to mention:', messageId);
    setJumpToMessageId(messageId);
    // Reset after a short delay to allow re-triggering the same message
    setTimeout(() => setJumpToMessageId(undefined), 1000);
  };

  // Force re-render debug
  const renderKey = `${selectedGroup?.chat_id}-${messages.length}`;
  console.log('Desktop render key:', renderKey);

  return (
    <div className="flex h-[600px] bg-gray-900 rounded-lg overflow-hidden">
      {/* Sidebar */}
      <div className="w-80 border-r border-gray-700 flex-shrink-0">
        <ChatSidebar 
          chatGroups={chatGroups}
          selectedGroup={selectedGroup}
          onSelectGroup={selectGroup}
          canCreateGroup={canCreateGroup}
          onCreateGroup={handleCreateGroup}
          user={user}
        />
      </div>

      {/* Main Chat Area - fills remaining space */}
      <div className="flex-1 min-w-0">
        {selectedGroup ? (
          <div key={renderKey} className="h-full">
            <ChatMessagesArea
              selectedGroup={selectedGroup}
              messages={messages}
              user={user}
              newMessage={newMessage}
              onMessageChange={setNewMessage}
              onSendMessage={handleSendMessage}
              canCreateGroup={canCreateGroup}
              onCreateGroup={handleCreateGroup}
              isGroupCreator={selectedGroup.created_by === user.id}
              highlightMessageId={jumpToMessageId || highlightMessageId}
              onJumpToMention={handleJumpToMention}
              replyingToMessageId={replyingToMessageId}
              onSetReplyingTo={setReplyingToMessageId}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-800">
            <div className="text-center">
              <p className="text-gray-300 mb-4">Select a chat group to start messaging</p>
              {canCreateGroup && (
                <Button
                  onClick={handleCreateGroup}
                  className="bg-sports-green hover:bg-sports-green/90 text-black"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Group
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Chat Modal */}
      <CreateChatModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGroupCreated={handleGroupCreated}
      />
    </div>
  );
};

export default DesktopChatLayout;
